import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
// react-grid-layout v2 ships the v1-compatible WidthProvider API under /legacy.
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import type { Layout, ResponsiveLayouts } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

export interface GridPanel {
  id: string;
  /** Default position/size on wide screens (12-col grid, units of rowHeight). */
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  render: () => ReactNode;
}

const COLS = { lg: 12, md: 12, sm: 6, xs: 2, xxs: 1 };
const BREAKPOINTS = { lg: 1280, md: 996, sm: 768, xs: 480, xxs: 0 };
const ROW_HEIGHT = 48;
const MARGIN: [number, number] = [16, 16];

// Narrow breakpoints stack panels full-width, one per row, preserving order.
function stack(panels: GridPanel[], cols: number): Layout {
  let y = 0;
  return panels.map((p) => {
    const item = { i: p.id, x: 0, y, w: cols, h: p.h, minH: p.minH ?? 3 };
    y += p.h;
    return item;
  });
}

function buildDefaults(panels: GridPanel[]): ResponsiveLayouts {
  const lg: Layout = panels.map((p) => ({
    i: p.id, x: p.x, y: p.y, w: p.w, h: p.h, minW: p.minW ?? 3, minH: p.minH ?? 3,
  }));
  return { lg, md: lg, sm: stack(panels, 6), xs: stack(panels, 2), xxs: stack(panels, 1) };
}

/**
 * Draggable + resizable layout state persisted per-user to localStorage.
 * `reset()` clears the saved layout and restores the panel defaults.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with its grid component (Fast Refresh dev-only)
export function useDashboardLayout(storageKey: string, panels: GridPanel[]) {
  const defaults = useMemo(() => buildDefaults(panels), [panels]);
  const [layouts, setLayouts] = useState<ResponsiveLayouts>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw) as ResponsiveLayouts;
    } catch { /* sandbox / corrupt */ }
    return defaults;
  });
  const onLayoutChange = useCallback(
    (_current: Layout, all: ResponsiveLayouts) => {
      setLayouts(all);
      try { localStorage.setItem(storageKey, JSON.stringify(all)); } catch { /* sandbox */ }
    },
    [storageKey],
  );
  const reset = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch { /* sandbox */ }
    setLayouts(buildDefaults(panels));
  }, [storageKey, panels]);
  return { layouts, onLayoutChange, reset };
}

/**
 * Grafana-style draggable/resizable panel grid. Drag by each panel's title bar
 * (PanelFrame draggable), resize from the bottom-right handle. Interactive
 * controls inside a panel never start a drag (draggableCancel).
 */
export function DashboardGrid({
  panels,
  layouts,
  onLayoutChange,
}: {
  panels: GridPanel[];
  layouts: ResponsiveLayouts;
  onLayoutChange: (current: Layout, all: ResponsiveLayouts) => void;
}) {
  return (
    <ResponsiveGridLayout
      className="layout"
      layouts={layouts}
      breakpoints={BREAKPOINTS}
      cols={COLS}
      rowHeight={ROW_HEIGHT}
      margin={MARGIN}
      containerPadding={[0, 0]}
      draggableHandle=".panel-drag-handle"
      draggableCancel=".MuiButtonBase-root,input,a,.MuiChip-root,.MuiSelect-select"
      onLayoutChange={onLayoutChange}
    >
      {panels.map((p) => (
        <div key={p.id} style={{ height: '100%' }}>
          {p.render()}
        </div>
      ))}
    </ResponsiveGridLayout>
  );
}
